import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, getContent, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { FadeIn, SectionTag } from "@/components/Reveal";
import { whatsappLink, WhatsAppIcon } from "@/components/FloatingWhatsApp";
import SEO from "@/components/SEO";

const EMPTY = { name: "", company: "", email: "", phone: "", product: "", quantity: "", message: "" };
const PRODUCT_CATEGORIES = ["Bituminous Products", "Specialty Petrochemicals", "Industrial Materials", "Fuels & Others"];

export default function Contact() {
  const [params] = useSearchParams();
  const [content, setContent] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ ...EMPTY, product: params.get("product") || "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getContent().then(setContent).catch(() => setContent({}));
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const productGroups = useMemo(
    () =>
      PRODUCT_CATEGORIES
        .map((cat) => ({ cat, items: products.filter((p) => p.category === cat) }))
        .filter((g) => g.items.length > 0),
    [products],
  );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. Our team will reach out within 1-2 business days.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not submit enquiry. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const contact = content?.contact;
  const inputCls =
    "w-full border-b border-white/15 bg-transparent py-3 text-sm text-white placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-brand";
  const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500";

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact Catalyst Petrochem — Bitumen Enquiries & Quotes"
        description="Request bulk bitumen quotes, technical data sheets or long-term supply contracts. Our sales desk responds within 1-2 business days."
        path="/contact"
      />
      <PageHeader
        index="06"
        tag="Contact & Enquiry"
        title="Talk bitumen with us."
        description="Bulk quotes, grade availability, technical data sheets or long-term supply contracts — send an enquiry and our team responds within 1-2 business days."
      />

      <section className="grid gap-14 px-6 py-20 md:grid-cols-12 md:px-12 md:py-28 lg:px-20">
        <div className="md:col-span-5">
          <SectionTag index="01" label="Reach Us" />
          {contact && (
            <div className="mt-10 space-y-10">
              <FadeIn className="flex gap-5">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-brand" />
                <div>
                  <p className={labelCls}>Head Office</p>
                  <p className="mt-2 max-w-xs text-sm leading-relaxed text-neutral-300" data-testid="contact-address">
                    {contact.address}
                  </p>
                </div>
              </FadeIn>
              <FadeIn delay={0.08} className="flex gap-5">
                <Phone className="mt-1 h-5 w-5 shrink-0 text-brand" />
                <div>
                  <p className={labelCls}>Phone</p>
                  {contact.phones.map((p) => (
                    <a
                      key={p}
                      href={`tel:${p.replace(/\s/g, "")}`}
                      data-testid="contact-phone"
                      className="mt-2 block text-sm text-neutral-300 transition-colors hover:text-brand"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </FadeIn>
              <FadeIn delay={0.16} className="flex gap-5">
                <Mail className="mt-1 h-5 w-5 shrink-0 text-brand" />
                <div>
                  <p className={labelCls}>Email</p>
                  {contact.emails.map((em) => (
                    <a
                      key={em}
                      href={`mailto:${em}`}
                      data-testid="contact-email"
                      className="mt-2 block text-sm text-neutral-300 transition-colors hover:text-brand"
                    >
                      {em}
                    </a>
                  ))}
                </div>
              </FadeIn>
              <FadeIn delay={0.24}>
                <a
                  href={whatsappLink(form.product)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="flex items-center gap-5 border border-[#25D366]/40 bg-[#25D366]/5 p-6 transition-colors duration-300 hover:bg-[#25D366]/15"
                >
                  <WhatsAppIcon className="h-6 w-6 shrink-0 text-[#25D366]" />
                  <div>
                    <p className={labelCls}>Instant Response</p>
                    <p className="mt-2 text-sm text-neutral-300">Ping us on WhatsApp — our sales desk replies in minutes.</p>
                  </div>
                </a>
              </FadeIn>
              <FadeIn delay={0.3} className="border border-white/10 bg-[#0C0C0C] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Statutory</p>
                <p className="mt-3 font-mono text-xs leading-relaxed text-neutral-400">
                  IEC 0517501902
                  <br />
                  GST 07AALFC9514G1ZC
                  <br />
                  LLP AAI-2954
                </p>
              </FadeIn>
            </div>
          )}
        </div>

        <div className="md:col-span-7">
          <SectionTag index="02" label="Enquiry / Quote Request" />
          <FadeIn delay={0.1}>
            <form onSubmit={submit} className="mt-10 grid gap-8 sm:grid-cols-2" data-testid="enquiry-form">
              <div>
                <label className={labelCls} htmlFor="enq-name">Name *</label>
                <input id="enq-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="enquiry-name-input" />
              </div>
              <div>
                <label className={labelCls} htmlFor="enq-company">Company</label>
                <input id="enq-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Organisation" data-testid="enquiry-company-input" />
              </div>
              <div>
                <label className={labelCls} htmlFor="enq-email">Email *</label>
                <input id="enq-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" data-testid="enquiry-email-input" />
              </div>
              <div>
                <label className={labelCls} htmlFor="enq-phone">Phone</label>
                <input id="enq-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" data-testid="enquiry-phone-input" />
              </div>
              <div>
                <label className={labelCls} htmlFor="enq-product">Product of Interest</label>
                <select
                  id="enq-product"
                  value={form.product}
                  onChange={set("product")}
                  className={`${inputCls} cursor-pointer bg-[#0A0A0A]`}
                  data-testid="enquiry-product-select"
                >
                  <option value="">General enquiry</option>
                  {productGroups.map((g) => (
                    <optgroup key={g.cat} label={g.cat}>
                      {g.items.map((p) => (
                        <option key={p.id} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="enq-qty">Quantity</label>
                <input id="enq-qty" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 500 MT / month" data-testid="enquiry-quantity-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="enq-msg">Message</label>
                <textarea id="enq-msg" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Delivery location, grade, timelines…" data-testid="enquiry-message-input" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-button"
                  className="group inline-flex items-center gap-3 bg-brand px-10 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-deep disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Submit Enquiry"}
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
